import { TrackingPageConfig, TrackingPageData, TrackingShipment, TrackingStatus } from './types';

/**
 * Sample data for the merchant preview and for tests.
 *
 * The preview iframe renders from these rather than from a live order, so a
 * merchant can theme their page before any shipment exists.
 */

export const DEFAULT_CONFIG: TrackingPageConfig = {
  version: 1,
  theme: {
    primaryColor: '#1565c0',
    backgroundColor: '#f6f7fb',
    surfaceColor: '#ffffff',
    textColor: '#0b1c30',
    fontFamily: 'system',
    cornerRadius: 'rounded',
  },
  branding: {
    logoUrl: null,
    logoHeight: 48,
    coverImageUrl: null,
    coverImagePosition: 'left',
    showPoweredBy: true,
  },
  sections: { hero: true, progress: true, timeline: true, items: true, ads: false, support: true },
  copy: { headline: null, supportEmail: null, supportUrl: null },
  ads: [],
};

const PRESENTATION: Record<TrackingStatus, Pick<TrackingShipment, 'statusLabel' | 'statusVariant' | 'statusTone' | 'nodeIndex'>> = {
  PRE_TRANSIT: { statusLabel: 'Label created', statusVariant: 'normal', statusTone: 'neutral', nodeIndex: 0 },
  IN_TRANSIT: { statusLabel: 'In transit', statusVariant: 'normal', statusTone: 'progress', nodeIndex: 1 },
  OUT_FOR_DELIVERY: { statusLabel: 'Out for delivery', statusVariant: 'normal', statusTone: 'progress', nodeIndex: 2 },
  DELIVERED: { statusLabel: 'Delivered', statusVariant: 'normal', statusTone: 'success', nodeIndex: 3 },
  AVAILABLE_FOR_PICKUP: { statusLabel: 'Ready for pickup', statusVariant: 'pickup', statusTone: 'success', nodeIndex: 2 },
  FAILURE: { statusLabel: 'Delivery attempt failed', statusVariant: 'exception', statusTone: 'warning', nodeIndex: 2 },
  ERROR: { statusLabel: 'Delivery exception', statusVariant: 'exception', statusTone: 'danger', nodeIndex: 1 },
  RETURN_TO_SENDER: { statusLabel: 'Returning to sender', statusVariant: 'exception', statusTone: 'warning', nodeIndex: 1 },
  CANCELLED: { statusLabel: 'Shipment cancelled', statusVariant: 'cancelled', statusTone: 'danger', nodeIndex: 0 },
  UNKNOWN: { statusLabel: 'Awaiting carrier scan', statusVariant: 'unknown', statusTone: 'neutral', nodeIndex: 0 },
};

export function fixtureShipment(status: TrackingStatus = 'IN_TRANSIT'): TrackingShipment {
  return {
    trackingNumber: '1Z999AA10123456784',
    trackingCompany: 'UPS',
    carrierTrackingUrl: 'https://www.ups.com/track?tracknum=1Z999AA10123456784',
    status,
    ...PRESENTATION[status],
    estimatedDeliveryDate: '2026-08-16',
    checkpoints: [
      {
        date: 'Tue, Aug 11',
        checkpoints: [
          { message: 'In Transit', description: 'Departed from facility', address: 'Seattle, WA, US', time: '2:05 PM' },
          { message: 'In Transit', description: 'Arrived at facility', address: 'Seattle, WA, US', time: '9:00 AM' },
        ],
      },
      {
        date: 'Mon, Aug 10',
        checkpoints: [
          { message: 'Pre Transit', description: 'Shipment label created', address: 'Portland, OR, US', time: '4:30 PM' },
        ],
      },
    ],
    items: [
      { title: 'Premium Wireless Headphones', quantity: 1, price: 199.0, imageUrl: null },
      { title: 'USB-C Cable', quantity: 2, price: 12.5, imageUrl: null },
    ],
  };
}

export function fixtureTrackingPageData(
  status: TrackingStatus = 'IN_TRANSIT',
  config: TrackingPageConfig = DEFAULT_CONFIG,
): TrackingPageData {
  return {
    storeId: 'preview-store',
    storeName: 'Acme Co',
    orderNumber: '#1001',
    recipientName: 'Ada Lovelace',
    branding: config,
    shipments: [fixtureShipment(status)],
  };
}
